import { useId, useMemo, useRef, useState } from 'react';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { formLabels } from '../types';
import type { DashboardDetail } from '../dashboardTypes';
import { formatMoney, todayIso } from '../format';
import { describeFailure, type Failure } from '../drawer/failures';
import { taxPaymentsGateway } from '../index';

const REFERENCE = /^[A-Za-z0-9._:/# -]{1,100}$/;
const LONG_NUMBER = /\d[\d\s-]{7,}\d/;

type Field = 'date' | 'reference' | 'note' | 'ack';

/**
 * Mark as Insufficient Funds (Kotapay payments only, offered by the server only while Pending Payment or Payment Processing). Records, by hand, that Kotapay or the
 * client's bank reported insufficient funds. It cannot be done for NatPay (its return arrives from its feed). It does not retry or pay anything, and the client
 * notification of the SRS is not sent yet (BACKEND GAP). Needs a recent authenticator-app check; the dialog explains it when the server asks for one.
 */
export function MarkInsufficientFundsDialog({ detail, companyName, onClose, onRecorded }: { detail: DashboardDetail; companyName: string | null; onClose: () => void; onRecorded: (updated: DashboardDetail) => void }) {
  const uid = useId();
  const { row } = detail;
  const [date, setDate] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [ack, setAck] = useState(false);
  const [touched, setTouched] = useState<ReadonlySet<Field>>(new Set());
  const [failure, setFailure] = useState<Failure | null>(null);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);

  const errors = useMemo(() => {
    const e: Partial<Record<Field, string>> = {};
    if (!date) e.date = 'Enter the date the insufficient funds was identified.';
    else if (date > todayIso()) e.date = 'The date can’t be in the future.';
    if (!reference.trim()) e.reference = 'Enter the Kotapay reference or reason code.';
    else if (!REFERENCE.test(reference.trim())) e.reference = 'Use 1 to 100 letters, digits or . _ : / # - characters.';
    if (!note.trim()) e.note = 'Enter an internal note.';
    else if (note.trim().length > 500) e.note = 'The note must be 500 characters or fewer.';
    else if (LONG_NUMBER.test(note)) e.note = 'Don’t enter an account or other long number in the note.';
    if (!ack) e.ack = 'Confirm that Kotapay or the client’s bank reported insufficient funds.';
    return e;
  }, [date, reference, note, ack]);
  const valid = Object.keys(errors).length === 0;
  const show = (f: Field) => (touched.has(f) ? errors[f] : undefined);
  const touch = (f: Field) => setTouched((t) => (t.has(f) ? t : new Set(t).add(f)));
  const err = (f: Field) => (show(f) ? <p className="jr-error" role="alert">{errors[f]}</p> : null);
  const edit = () => { if (failure) setFailure(null); };

  async function save() {
    setTouched(new Set<Field>(['date', 'reference', 'note', 'ack']));
    if (!valid || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setFailure(null);
    try {
      onRecorded(await taxPaymentsGateway.recordInsufficientFunds(row.id, { dateIdentified: date, reasonReference: reference.trim(), internalNote: note.trim(), acknowledged: ack }));
    } catch (e) {
      setFailure(describeFailure(e, 'Insufficient funds couldn’t be recorded. Nothing was changed. Try again.'));
      setBusy(false);
      submitting.current = false;
    }
  }

  return (
    <ModalDialog
      title="Mark as Insufficient Funds" size="sm" className="tpd-modal" busy={busy} onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="jr-btn jr-btn--primary" disabled={busy || !valid} onClick={() => void save()}>{busy ? 'Saving…' : 'Mark as Insufficient Funds'}</button>
        </>
      )}
    >
      <p className="tpd-muted">{companyName ? `${companyName} · ` : ''}{row.agency} — {row.jurisdiction} · {formLabels[row.formCode] ?? row.formCode} · {formatMoney(row.amount)}</p>
      <p className="tpd-muted">Use this when Kotapay or the client&rsquo;s bank reported that the account could not cover this payment. It does not retry or pay anything.</p>
      <div className="jr-field">
        <label htmlFor={`${uid}-date`}>Date identified <span className="jr-req" aria-hidden="true">*</span></label>
        <input id={`${uid}-date`} data-autofocus type="date" value={date} max={todayIso()} aria-required="true" aria-invalid={show('date') ? true : undefined} onBlur={() => touch('date')} onChange={(e) => { setDate(e.target.value); edit(); }} />
        {err('date')}
      </div>
      <div className="jr-field">
        <label htmlFor={`${uid}-ref`}>Kotapay reference or reason <span className="jr-req" aria-hidden="true">*</span></label>
        <input id={`${uid}-ref`} value={reference} maxLength={100} aria-required="true" aria-invalid={show('reference') ? true : undefined} onBlur={() => touch('reference')} onChange={(e) => { setReference(e.target.value); edit(); }} />
        {err('reference')}
      </div>
      <div className="jr-field">
        <label htmlFor={`${uid}-note`}>Internal note <span className="jr-req" aria-hidden="true">*</span></label>
        <textarea id={`${uid}-note`} rows={3} maxLength={500} value={note} aria-required="true" aria-invalid={show('note') ? true : undefined} onBlur={() => touch('note')} onChange={(e) => { setNote(e.target.value); edit(); }} />
        {err('note')}
      </div>
      <label className="tpd-check">
        <input type="checkbox" checked={ack} aria-required="true" onChange={(e) => { setAck(e.target.checked); setTouched((t) => new Set(t).add('ack')); edit(); }} />
        <span>I confirm that Kotapay or the client&rsquo;s bank reported insufficient funds for this tax payment. This action is audited.</span>
      </label>
      {err('ack')}
      {failure && (
        <div className="tpd-form-error" role="alert" data-testid="insufficient-error">
          <p>{failure.message}</p>
        </div>
      )}
    </ModalDialog>
  );
}
